import React from 'react';
import { useTranslation } from 'react-i18next';
import { getProfileIdentity } from '@/auth/session';
import { useElementHeight } from '@/hooks/useElementHeight';
import { ClassChip } from '@/pages/Overview/ClassChip';
import { useSameLevelClasses } from '@/pages/Overview/hooks/useSameLevelClasses';
import type { SameLevelClass } from '@/types/ratings';

const CLIP_PADDING_PX = 16;

interface Props {
  isVisible: boolean;
  isAnimated?: boolean;
  className?: string;
  value: number[];
  onChange: (sameLevelClassIds: number[]) => void;
}

export const SameLevelClassChips: React.FC<Props> = ({
  isVisible,
  isAnimated = true,
  className = '',
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const { data: classes = [] } = useSameLevelClasses({
    enabled: isVisible,
  });
  const { ref: contentRef, height: contentHeight } = useElementHeight();
  const ownClassId = getProfileIdentity()?.classId;

  const isAllSelected = value.length === 0;
  const hasClasses = classes.length > 0;
  const isExpanded = isVisible && hasClasses;

  const getClipHeight = () => {
    if (!isExpanded) {
      return CLIP_PADDING_PX * 2;
    }

    if (contentHeight === undefined) {
      return 'auto';
    }

    return contentHeight + CLIP_PADDING_PX * 2;
  };

  const getLabel = ({ id, name }: SameLevelClass) =>
    id === ownClassId ? t('overview.ratings.ownClass', { name }) : name;

  const toggleClass = (id: number) => {
    const isSelected = value.includes(id);

    if (isSelected) {
      onChange(value.filter((selectedId) => selectedId !== id));
      return;
    }

    onChange([...value, id]);
  };

  const chipList = (
    <div className="flex flex-wrap gap-2">
      <ClassChip
        label={t('overview.ratings.allClasses')}
        isSelected={isAllSelected}
        onClick={() => onChange([])}
      />
      {classes.map((sameLevelClass) => (
        <ClassChip
          key={sameLevelClass.id}
          label={getLabel(sameLevelClass)}
          isSelected={value.includes(sameLevelClass.id)}
          onClick={() => toggleClass(sameLevelClass.id)}
        />
      ))}
    </div>
  );

  if (!isAnimated) {
    return (
      <div className={className}>
        {isExpanded && <div className="pt-2">{chipList}</div>}
      </div>
    );
  }

  return (
    <div inert={!isExpanded} className={className}>
      <div
        style={{ height: getClipHeight() }}
        className={`-m-4 overflow-hidden p-4 transition-[height,opacity] duration-300 ease-out ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
      >
        <div ref={contentRef} className="pt-2">
          {chipList}
        </div>
      </div>
    </div>
  );
};
